import * as TweenJS from "slotworks/utils/tween";
import { Sprite } from "pixi.js";
import Layers from "slotworks/graphics/layers/layers";
import Components from "slotworks/components/components";
import canvasManager, { Orientation } from "slotworks/graphics/canvas/canvas-manager";
import deviceInfo from "slotworks/utils/device-info";
import Timer from "slotworks/utils/timer";
import { HideBaseLayers } from "commands/base-layers-commands";
import gameState from "slotworks/model/game-state";

export default class BackgroundComponent {

    private layer: Layers;

    private ligths: Sprite[] = [];
    private columnLeft: Sprite;
    private columnRight: Sprite;

    private spotLights1: Sprite;
    private spotLights2: Sprite;

    private backSpotLights1: Sprite = null;
    private backSpotLights2: Sprite = null;

    private lightsObject = { alpha: 0 };
    private sideObject = { alpha: 0 };
    private spotsObject = { alpha: 0 };
    private side: number = 800;

    private reelStopped: boolean = false;
    private lampRotationObject = { rotation: 0 };

    private lightTween: TweenJS.Tween<{ alpha: number }>;
    private rotationTween: TweenJS.Tween<{ rotation: number }>;

    // background illumination alpha
    private anticipating: boolean = false;
    private anticipationStopping: boolean = false;
    private illuminationObject = { alpha: 0 };
    private anticipationTween: TweenJS.Tween<{ alpha: number }> = null;

    private alphaTweens: TweenJS.Tween<{ alpha: number }>[] = [];
    private lightTimers: number[] = [];

    constructor() {
        this.layer = Layers.Background;

        this.layer.onSceneEnter.add(this.setup);
        this.layer.onSceneExit.add(() => {
            this.clean();
        })

        Components.matrix.startSignal.add(this.reelStart);

        Components.matrix.landSignal.add(this.reelStop);

        this.setup();

        const divIlluminated = document.querySelector("#Background #default .illumination");
        const divFsIlluminated = document.querySelector("#Background #freespins .illumination");

        if (divIlluminated) {
            (divIlluminated as HTMLDivElement).style.opacity = "0";
        }

        if (divFsIlluminated) {
            (divFsIlluminated as HTMLDivElement).style.opacity = "0";
        }
        
    }

    public start() {
        if (canvasManager.orientation === Orientation.LANDSCAPE) {
            this.startLightTweens();
            this.startSideTween();
        } else {
            this.startLightTweens();
        }
        this.spotsFlicker();
    }

    public startBonus() {
        this.clean();
        this.startBonusLightsWave();
    }

    public startBonusSpin() {
        this.clean();
        this.randomLights();
    }

    public startBonusSelection() {
        this.clean();
        this.bonusSelectionSequence();
    }

    public stop() {
        this.clean();
    }

    public setFastSideFlicker() {
        this.side = 400;
    }

    public setDefaultSideFlicker() {
        this.side = 1000;
    }

    /**
     * Starts illuminated bacground anticipation
     */
    public startAnticipation() {
        if (!this.anticipating) {
            this.anticipating = true;
            this.anticipationStopping = false;
            this.illuminationObject.alpha = 0;

            if (!this.anticipationTween) {
                this.anticipationTween = new TweenJS.Tween(this.illuminationObject)
                    .to({ alpha: 1 }, 1000)
                    .onUpdate(this.updateIllumination)
                    .onRepeat(this.repeatIllumination)
                    .yoyo(true)
                    .repeat(Infinity)
                    .start();
            } else {
                this.anticipationTween.start();
            }

            this.updateIllumination();

            const illuminated = this.layer.getSprite("base_illuminated");
            if (illuminated) {
                illuminated.visible = true;
            }
        }
    }

    /**
     * Stops illuminated bacground anticipation
     */
    public stopAnticipation() {
        this.anticipating = false;
        this.anticipationStopping = true;
    }

    private setup = (name?: string) => {
        this.clean();
        if (name === "bonus") {
            this.setupBonus();
            return;
        }
        if (name !== "default" && name !== "freespins" && name != "gameintro")
            return;

        if (deviceInfo.isDesktop || canvasManager.orientation === Orientation.LANDSCAPE) {
            this.ligths = [];
            for (let i = 1; i <= 4; i++) {
                const light = this.layer.getSprite("Light_" + i);
                if (light) {
                    this.ligths.push(light);
                }
            }
            this.columnLeft = this.layer.getSprite("Column_Lights_Off_Left");
            this.columnRight = this.layer.getSprite("Column_Lights_Off_Right");

            this.spotLights1 = this.layer.getSprite("SpotLights_base1") || this.layer.getSprite("SpotLights_fs1"); // left
            this.spotLights2 = this.layer.getSprite("SpotLights_base2") || this.layer.getSprite("SpotLights_fs2"); // right

            // this.spotLights2.anchor.set(0.66, 0.2);
            this.spotLights2.anchor.set(0.66, 0.1);
            this.spotLights2.x += this.spotLights2.width * this.spotLights2.anchor.x;
            this.spotLights2.y += this.spotLights2.height * this.spotLights2.anchor.y;

            // this.spotLights1.anchor.set(0.35, 0.2);
            this.spotLights1.anchor.set(0.35, 0.1);
            this.spotLights1.x += this.spotLights1.width * this.spotLights1.anchor.x;
            this.spotLights1.y += this.spotLights1.height * this.spotLights1.anchor.y;

            this.backSpotLights1 = null;
            this.backSpotLights2 = null;
        } else {
            const illuminated = this.layer.getSprite("base_illuminated") || this.layer.getSprite("fs_illuminated");
            if (illuminated) illuminated.visible = false;

            this.ligths = [];
            for (let i = 1; i <= 4; i++) {
                const light = this.layer.getSprite("Light_" + i);
                if (light) {
                    this.ligths.push(light);
                }
            }

            this.spotLights1 = this.layer.getSprite("spot_base_front_2") || this.layer.getSprite("spot_fs_front_2"); // left
            this.spotLights2 = this.layer.getSprite("spot_base_front_1") || this.layer.getSprite("spot_fs_front_1"); // right

            this.backSpotLights1 = this.layer.getSprite("spot_base_back_2") || this.layer.getSprite("spot_fs_back_2"); // left
            this.backSpotLights2 = this.layer.getSprite("spot_base_back_1") || this.layer.getSprite("spot_fs_back_1"); // right

            this.spotLights2.anchor.set(0.66, 0.2);
            this.spotLights2.x += this.spotLights2.width * this.spotLights2.anchor.x;
            this.spotLights2.y += this.spotLights2.height * this.spotLights2.anchor.y;

            this.spotLights1.anchor.set(0.35, 0.2);
            this.spotLights1.x += this.spotLights1.width * this.spotLights1.anchor.x;
            this.spotLights1.y += this.spotLights1.height * this.spotLights1.anchor.y;

            if (deviceInfo.widePortrait) {
                this.backSpotLights2.anchor.set(0.5, 0.2);
                this.backSpotLights2.x += this.backSpotLights2.anchor.x * this.backSpotLights2.width;
                this.backSpotLights2.y += this.backSpotLights2.anchor.y * this.backSpotLights2.height;

                this.backSpotLights1.anchor.set(0.5, 0.2);
                this.backSpotLights1.x += this.backSpotLights1.anchor.x * this.backSpotLights1.width;
                this.backSpotLights1.y += this.backSpotLights1.anchor.y * this.backSpotLights1.height;
            } else {
                this.backSpotLights2.anchor.set(1, 0);
                this.backSpotLights2.x += this.backSpotLights2.width;
            }
        }

        this.start();
    }

    private clean() {
        this.ligths = null;
        if (this.lightTween)
            this.lightTween.stop();
        this.stopLightTimers();
        this.stopAlphaTweens();
    }

    private setupBonus() {
        // Lights_R_1, Lights_L_1 ... Lights_L_4
        // Wall_Light_R_2, Wall_Light_R_3, Wall_Light_L_2
        // Wall_Light_R_4_&_Reflection
        // Wall_Light_L_3_&_Reflection
        // Wall_Light_R_5_&_Reflection
        // Wall_Light_L_4_&_Reflection
        // Wall_Light_R_6_&_Reflection
        // Wall_Light_L_5_&_Reflection
        // Backlight_1
        // Backlight_2
    }

    private lightElementUp(name: string) {
        const sprite = Layers.Background.getSprite(name);
        if (sprite) {
            new TweenJS.Tween(sprite)
                .to({ alpha: 1 }, 100)
                .onComplete(() => {
                    new TweenJS.Tween(sprite)
                        .to({ alpha: 1 }, 200)
                        .onComplete(() => {
                            new TweenJS.Tween(sprite)
                                .delay(300)
                                .to({ alpha: 0 }, 400)
                                .start();
                        })
                        .start();
                })
                .start();
        }
    }

    private setVisibility(name: string, visible: boolean = false) {
        const sprite = Layers.Background.getSprite(name);
        if (sprite) {
            sprite.visible = visible;
            sprite.alpha = 1;
        }
    }

    private startLampRotationTweens() {
        if (this.lampRotationObject.rotation <= -40) {
            this.innerStartLampRotationTweens();
        } else {
            this.rotationTween = new TweenJS.Tween(this.lampRotationObject)
                .to({ rotation: -40 }, 400)
                .onUpdate(this.onLampRotationUpdate)
                .onComplete(this.innerStartLampRotationTweens)
                .start();
        }
    }

    private stopLampRotationTweens(instant: boolean = false) {
        if (instant) {
            this.rotationTween.stop();
        } else {
            this.rotationTween.stop();
            this.rotationTween = new TweenJS.Tween(this.lampRotationObject)
                .to({ rotation: 0 }, Math.abs(this.lampRotationObject.rotation) * 12)
                .onUpdate(this.onLampRotationUpdate)
                .start();
        }
    }

    private innerStartLampRotationTweens = () => {
        this.lampRotationObject.rotation = -40;
        this.rotationTween = new TweenJS.Tween(this.lampRotationObject)
            .to({ rotation: 40 }, 1000)
            .repeat(Infinity)
            .yoyo(true)
            .onRepeat(this.onLampRotationRepeat)
            .onUpdate(this.onLampRotationUpdate)
            .start();
    }

    private onLampRotationUpdate = () => {
        if (this.spotLights1) {
            this.spotLights1.angle = this.lampRotationObject.rotation;
        }
        if (this.spotLights2) {
            this.spotLights2.angle = -this.lampRotationObject.rotation;
        }
        if (this.backSpotLights1) {
            this.backSpotLights1.angle = this.lampRotationObject.rotation;
        }
        if (this.backSpotLights2) {
            this.backSpotLights2.angle = -this.lampRotationObject.rotation;
        }
    }

    private onLampRotationRepeat = () => {
        if (this.reelStopped && this.rotationTween && this.rotationTween.isPlaying && this.lampRotationObject.rotation < 40) {
            this.rotationTween.stop();
        }
    }

    private repeatIllumination = () => {
        if (this.anticipationStopping && this.illuminationObject.alpha != 1) {
            this.anticipationStopping = false;

            if (this.anticipationTween)
                this.anticipationTween.stop();

            const illuminated = this.layer.getSprite("base_illuminated");
            if (illuminated) {
                illuminated.alpha = 0;// this.illuminationObject.alpha;
                illuminated.visible = false;
            } else {
                const scene = gameState.getCurrentGame().inFreespinsGame ? "#freespins" : "#default";
                const divIlluminated = document.querySelector(`#Background ${scene} .illumination`);

                if (divIlluminated) {
                    (divIlluminated as HTMLDivElement).style.opacity = "0";
                }
            }
        }
    }

    private updateIllumination = () => {
        const illuminated = this.layer.getSprite("base_illuminated") || this.layer.getSprite("fs_illuminated");
        if (illuminated) {
            illuminated.alpha = this.illuminationObject.alpha;
        } else {
            const scene = gameState.getCurrentGame().inFreespinsGame ? "#freespins" : "#default";
            const divIlluminated = document.querySelector(`#Background ${scene} .illumination`);

            if (divIlluminated) {
                (divIlluminated as HTMLDivElement).style.opacity = this.illuminationObject.alpha.toString();
            }
        }
    }

    private startLightTweens() {
        if (!this.ligths)
            return;
        const update = () => {
            for (let light of this.ligths) {
                light.alpha = this.lightsObject.alpha;
            }
        }
        const on = new TweenJS.Tween(this.lightsObject).to({ alpha: 1 }, 200).onUpdate(update);
        const off = new TweenJS.Tween(this.lightsObject).to({ alpha: 0 }, 430).onUpdate(update);

        on.chain(off);
        off.chain(on);

        on.start();

        // this.lightTween = on;
        this.alphaTweens.push(on);
        this.alphaTweens.push(off);
    }

    private startSideTween() {
        this.sideFlicker(200);
    }

    private spotsFlicker = () => {
        const update = () => {
            this.spotLights1.alpha = this.spotsObject.alpha;
            this.spotLights2.alpha = this.spotsObject.alpha;
            if (this.backSpotLights1 && this.backSpotLights2) {
                this.backSpotLights1.alpha = this.spotsObject.alpha;
                this.backSpotLights2.alpha = this.spotsObject.alpha;
            }
        }
        this.spotsObject.alpha = 0.6;
        this.alphaTweens.push(new TweenJS.Tween(this.spotsObject)
        .to({alpha: 1}, 600)
        .onUpdate(update)
        .repeat(Infinity)
        .yoyo(true)
        .start());
    }

    private sideFlicker = (time: number) => {
        if (!this.columnLeft || !this.columnRight)
            return;
        const update = () => {
            if (!this.columnLeft || !this.columnRight)
                return;
            this.columnLeft.alpha = this.sideObject.alpha;
            this.columnRight.alpha = this.sideObject.alpha;
        }
        this.sideObject.alpha = 0;
        this.alphaTweens.push(new TweenJS.Tween(this.sideObject)
            .to({ alpha: 1 }, time)
            .onUpdate(update)
            .repeat(1)
            .yoyo(true)
            .onComplete(() => {
                const next = this.side / 2 + Math.random() * this.side;
                this.lightTimers.push(Timer.setTimeout(() => this.sideFlicker(next), 200));
            })
            .start());
    }

    private reelStart = () => {
        this.reelStopped = false;

        this.startLampRotationTweens();

    }

    private reelStop = (reel: number) => {
        if (reel === 5) {
            this.stopLampRotationTweens();
        }
    }

    // bonus lights
    private startBonusLightsWave = () => {
        this.currentTopLight = 1;
        this.currentLight = 0;
        
        this.lightNextBonusTopLight();
        this.lightTimers.push(Timer.setTimeout(this.lightNextBonusLight, 100));

        this.lightTimers.push(Timer.setTimeout(this.startBonusLightsWave, 2000));
    }

    private currentLight: number = 0;
    private lightNextBonusLight = () => {
        const wave = LIGHTS[this.currentLight];
        for (let name of wave) {
            this.lightElementUp(name);
        }
        if (this.currentLight < LIGHTS.length - 1) {
            this.currentLight++;
            this.lightTimers.push(Timer.setTimeout(this.lightNextBonusLight, 200));
        }
    }

    private currentTopLight: number = 1; // from 1 to 4
    private lightNextBonusTopLight = () => {
        // Lights_L_1
        // Lights_R_1
        this.lightElementUp("Lights_L_" + this.currentTopLight);
        this.lightElementUp("Lights_R_" + this.currentTopLight);
        if (this.currentTopLight <= 4) {
            this.currentTopLight++;
            this.lightTimers.push(Timer.setTimeout(this.lightNextBonusTopLight, 400));
        }
    }

    private stopLightTimers() {
        for (const t of this.lightTimers) {
            Timer.clearTimeout(t);
        }
        this.lightTimers.length = 0;
    }

    private stopAlphaTweens() {
        for (const t of this.alphaTweens) {
            if(t) {
                t.stop();
                TweenJS.remove(t);
            }
        }
    }

    private lightUpAll = () => {
        for (let name of ALL_LIGHTS) {
            this.setVisibility(name, true);
        }
    }

    private lightOffAll = () => {
        for (let name of ALL_LIGHTS) {
            this.setVisibility(name, false);
        }
    }

    private fadeOutAll = () => {
        const obj = { alpha: 1 }
        this.alphaTweens.push(new TweenJS.Tween(obj)
            .to({ alpha: 0 }, 300)
            .onUpdate(() => {
                for (let name of ALL_LIGHTS) {
                    const sprite = Layers.Background.getSprite(name);
                    if (sprite) {
                        sprite.alpha = obj.alpha;
                    }
                }
            })
            .start());
    }

    private bonusSelectionSequence = () => {
        this.lightTimers.push(Timer.setTimeout(this.lightUpAll, 300));
        this.lightTimers.push(Timer.setTimeout(this.lightOffAll, 600));
        this.lightTimers.push(Timer.setTimeout(this.lightUpAll, 700));
        this.lightTimers.push(Timer.setTimeout(this.lightOffAll, 900));
        this.lightTimers.push(Timer.setTimeout(this.lightUpAll, 1000));
        this.lightTimers.push(Timer.setTimeout(this.lightOffAll, 1200));
        this.lightTimers.push(Timer.setTimeout(this.lightUpAll, 1400));
        // this.lightTimers.push(Timer.setTimeout(this.lightOffAll, time += 300));

        this.lightTimers.push(Timer.setTimeout(this.fadeOutAll, 1800));

        this.lightTimers.push(Timer.setTimeout(this.lightUpAll, 2500));
        this.lightTimers.push(Timer.setTimeout(this.lightOffAll, 2800));
        this.lightTimers.push(Timer.setTimeout(this.lightUpAll, 2900));
        this.lightTimers.push(Timer.setTimeout(this.lightOffAll, 3100));

        this.lightTimers.push(Timer.setTimeout(this.bonusSelectionSequence, 3300))
    }

    private switchOnOff(sprite: Sprite) {
        if (sprite) {
            sprite.visible = !sprite.visible;
            sprite.alpha = 1;
            this.lightTimers.push(Timer.setTimeout(() => {
                this.switchOnOff(sprite);
            }, Math.random() * 300 + 100));
        }
    }

    private randomLights() {
        for (let name of ALL_LIGHTS) {
            const sprite = Layers.Background.getSprite(name);
            if (!sprite)
                return;
            sprite.visible = Math.random() < 0.5;
            this.lightTimers.push(Timer.setTimeout(() => {
                this.switchOnOff(sprite);
            }, Math.random() * 300 + 100));
        }
    }

}

// waves to light
const LIGHTS: string[][] = [
    [
        "Wall_Light_L_2", "Wall_Light_R_2",
    ],
    [
        "Wall_Light_R_3",
    ],
    [
        "Wall_Light_R_4_&_Reflection", "Wall_Light_L_3_&_Reflection"
    ],
    [
        "Wall_Light_L_4_&_Reflection", "Wall_Light_R_5_&_Reflection"
    ],
    [
        "Wall_Light_L_5_&_Reflection", "Wall_Light_R_6_&_Reflection"
    ],
    [
        "Backlight_1",
    ],
    [
        "Backlight_2",
    ]
]

const ALL_LIGHTS: string[] = [
    "Wall_Light_L_2", "Wall_Light_R_2", "Wall_Light_R_3",
    "Wall_Light_R_4_&_Reflection", "Wall_Light_L_3_&_Reflection",
    "Wall_Light_L_4_&_Reflection", "Wall_Light_R_5_&_Reflection",
    "Wall_Light_L_5_&_Reflection", "Wall_Light_R_6_&_Reflection",
    "Lights_L_1", "Lights_R_1", "Lights_L_2", "Lights_R_2",
    "Lights_L_3", "Lights_R_3", "Lights_L_4", "Lights_R_4",
]